import { Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Inject, Injectable } from '../../injection';

import { EmptyResponse } from '../data/empty-response.do';
import { PagedResults } from '../data/paged-results.model';
import { SerializableObjectList } from '../data/serializable-object-list.model';
import { UserEmail } from '../data/user-email.do';
import { UserToken } from '../data/user-token.do';
import { User } from '../data/user.do';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { MobizServerApi } from '../other/mobiz-server-api.enum';

import { IUserService, UserQueryParameters } from './user.interface';

@Injectable()
export class UserService implements IUserService {
  constructor(
    @Inject(IMobizHttpService) private http: IMobizHttpService,
  ) {
  }

  /**
   * @inheritdoc
   */
  public createUser(user: User): Observable<User> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.User,
      body: JSON.stringify(user),
    };

    return this.http.post(new User(), config);
  }

  /**
   *  @inheritdoc
   */
  public getUser(userId: string) {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.User,
      urlParameters: {
        userId: userId,
      },
    };

    return this.http.get(new User(), config);
  }

  /**
   * @inheritdoc
   */
  public updateUser(user: User): Observable<User> {
    let config: ApiEndpointConfiguration = {
      body: JSON.stringify(user),
      serverApi: MobizServerApi.User,
      urlParameters: {
        userId: user.id,
      },
    };

    return this.http.put(new User(), config);
  }

  /**
   * @inheritdoc
   */
  public deleteUser(userId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.User,
      urlParameters: {
        userId: userId,
      },
    };

    return this.http.delete(new User(), config);
  }

  /**
   * @inheritdoc
   */
  public getAll(queryParameters: UserQueryParameters): Observable<PagedResults<User>> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AllUsers,
      queryParameters: queryParameters
    };
    let pagedUser = new PagedResults<User>(User);
    return this.http.get(pagedUser, config).pipe(map((response: PagedResults<User>) => {
      return response;
    }));
  }

  /**
   * @inheritdoc
   */
  public addEmail(userId: string, email: string): Observable<UserEmail> {
    return this.getUser(userId).pipe(concatMap((user: User) => {
      let data = {
        email: email,
        userId: user.id,
        verified: false,
      };

      let config: ApiEndpointConfiguration = {
        serverApi: MobizServerApi.AddUserEmail,
        body: JSON.stringify(data),
        urlParameters: {
          userId: user.id
        }
      };

      return this.http.post(new UserEmail(), config);
    }));
  }

  /**
   * @inheritdoc
   */
  public removeEmail(userId: string, email: string): Observable<UserEmail> {
    return this.getUser(userId).pipe(concatMap((user: User) => {
      let config: ApiEndpointConfiguration = {
        serverApi: MobizServerApi.RemoveUserEmail,
        urlParameters: {
          userId: user.id,
          email: email
        }
      };

      return this.http.delete<UserEmail>(new UserEmail(), config);
    }));
  }

  /**
   * @inheritdoc
   */
  public getTokens(userId: string): Observable<SerializableObjectList<UserToken>> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.UserTokens,
      urlParameters: {
        userId: userId,
      },
    };

    let pagedTokens = new SerializableObjectList<UserToken>(UserToken);
    return this.http.get(pagedTokens, config);
  }

  /**
   * @inheritdoc
   */
  public deleteToken(tokenId: string): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.UserToken,
      urlParameters: {
        tokenId: tokenId,
      },
    };

    return this.http.delete(new EmptyResponse(), config);
  }
}
